import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axiosInstance from "../../../../hooks/axiosInstance";
import {
  Button,
  Form,
  Modal,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";

const RECIPIENT_TYPE_OPTIONS = [
  { value: "base_user", label: "Base User" },
  { value: "account", label: "Account" },
  { value: "product", label: "Product" },
];

const SCOPE_OPTIONS_DEFAULT = [
  { value: "all", label: "All" },
  { value: "specific", label: "Specific" },
];

const SCOPE_OPTIONS_PRODUCT = [
  { value: "all", label: "All Products" },
  { value: "specific", label: "Specific Product" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CreateMessage({ show, closeModal, refreshPage }) {
  const [formData, setFormData] = useState({
    recipient_type: "",
    recipient_scope: "",
    subject: "",
    body: "",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [optionsByType, setOptionsByType] = useState({
    base_user: null,
    account: null,
    product: null,
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      recipient_type: "",
      recipient_scope: "",
      subject: "",
      body: "",
    });
    setSelectedIds([]);
    setCcEmails([]);
    setValidationError({});
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  // Lazily fetch the dropdown options for a recipient type (reusing the
  // existing list endpoints) the first time it's needed.
  const ensureOptionsLoaded = async (type) => {
    if (!type || optionsByType[type]) {
      return;
    }

    setOptionsLoading(true);

    try {
      let options = [];

      if (type === "base_user") {
        const response = await axiosInstance.get(
          "/user/base_user/fetch_base_user_no_paginate"
        );
        options = (response.data.data.base_user || []).map((user) => ({
          value: user.base_user_id ?? user.id,
          label: `${user.first_name} ${user.last_name} (${user.email})`,
        }));
      } else if (type === "account") {
        const response = await axiosInstance.get(
          "/user/account/getAccountWithoutPaginate"
        );
        options = (response.data.data.account || []).map((account) => ({
          value: account.id,
          label: account.company_name
            ? `${account.company_name} (${account.domain_name})`
            : account.domain_name,
        }));
      } else if (type === "product") {
        const response = await axiosInstance.get(
          "/user/product/getProductWithoutPaginate"
        );
        options = (response.data.data.products || []).map((product) => ({
          value: product.id,
          label: product.product_name,
        }));
      }

      setOptionsByType((prev) => ({ ...prev, [type]: options }));
    } catch (error) {
      toast.error("Failed to load recipient options.");
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (formData.recipient_type) {
      ensureOptionsLoaded(formData.recipient_type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.recipient_type]);

  const handleRecipientTypeChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      recipient_type: value,
      recipient_scope: "",
    }));
    setSelectedIds([]);
  };

  const handleScopeChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, recipient_scope: value }));
    setSelectedIds([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const currentOptions = optionsByType[formData.recipient_type] || [];
  const isProductType = formData.recipient_type === "product";
  const showSpecificSelector =
    formData.recipient_scope === "specific" && formData.recipient_type;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationError({});

    try {
      const recipientIds =
        formData.recipient_scope === "specific"
          ? selectedIds.map((option) => option.value)
          : [];

      const payload = {
        subject: formData.subject,
        body: formData.body,
        recipient_type: formData.recipient_type,
        recipient_scope: formData.recipient_scope,
        recipient_ids: recipientIds,
        cc: ccEmails.map((opt) => opt.value),
      };

      const response = await axiosInstance.post("/user/messaging/send", payload);

      handleClose();
      refreshPage();

      const recipientCount = response?.data?.data?.message?.recipient_count ?? 0;
      toast.success(`Message queued for ${recipientCount} recipient(s)!`);
    } catch (error) {
      if (error.response && error.response.data.errors) {
        setValidationError(error.response.data.errors);
        toast.error("Please fix the errors and try again.");
      } else {
        toast.error(
          error.response?.data?.message || "An error occurred while sending the message."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="CreateMessage">
      <Modal show={show} onHide={handleClose} dialogClassName="modal-lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Compose Message</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6} lg={6} xs={12} sm={12} className="mb-2">
                <Form.Group className="mb-3" controlId="formRecipientType">
                  <Form.Label>Recipient Type</Form.Label>
                  <Form.Select
                    name="recipient_type"
                    value={formData.recipient_type}
                    onChange={handleRecipientTypeChange}
                    isInvalid={!!validationError.recipient_type}
                    required
                  >
                    <option value="">-- Select Recipient Type --</option>
                    {RECIPIENT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {validationError.recipient_type && (
                    <Form.Control.Feedback type="invalid">
                      {validationError.recipient_type[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6} lg={6} xs={12} sm={12} className="mb-2">
                <Form.Group className="mb-3" controlId="formRecipientScope">
                  <Form.Label>Recipients</Form.Label>
                  <Form.Select
                    name="recipient_scope"
                    value={formData.recipient_scope}
                    onChange={handleScopeChange}
                    isInvalid={!!validationError.recipient_scope}
                    disabled={!formData.recipient_type}
                    required
                  >
                    <option value="">-- Select --</option>
                    {(isProductType
                      ? SCOPE_OPTIONS_PRODUCT
                      : SCOPE_OPTIONS_DEFAULT
                    ).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {validationError.recipient_scope && (
                    <Form.Control.Feedback type="invalid">
                      {validationError.recipient_scope[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {showSpecificSelector && (
                <Col md={12} lg={12} xs={12} sm={12} className="mb-2">
                  <Form.Group className="mb-3" controlId="formRecipientIds">
                    <Form.Label>
                      {isProductType ? "Select Product" : "Select Recipients"}
                    </Form.Label>
                    <Select
                      isMulti={!isProductType}
                      isLoading={optionsLoading}
                      options={currentOptions}
                      value={selectedIds}
                      onChange={(selected) =>
                        setSelectedIds(
                          isProductType
                            ? selected
                              ? [selected]
                              : []
                            : selected || []
                        )
                      }
                      placeholder={
                        isProductType ? "Search product..." : "Search..."
                      }
                    />
                    {validationError.recipient_ids && (
                      <small className="text-danger my-1 d-block">
                        {validationError.recipient_ids[0]}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              )}

              <Col md={12} lg={12} xs={12} sm={12} className="mb-2">
                <Form.Group className="mb-3" controlId="formSubject">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    isInvalid={!!validationError.subject}
                    required
                  />
                  {validationError.subject && (
                    <Form.Control.Feedback type="invalid">
                      {validationError.subject[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={12} lg={12} xs={12} sm={12} className="mb-2">
                <Form.Group className="mb-3" controlId="formCc">
                  <Form.Label>
                    CC <span className="text-muted fw-normal">(optional — type an email and press Enter)</span>
                  </Form.Label>
                  <CreatableSelect
                    isMulti
                    isClearable
                    placeholder="Add CC email addresses..."
                    value={ccEmails}
                    onChange={(selected) => {
                      setCcEmails(selected || []);
                      setValidationError((prev) => ({ ...prev, cc: undefined }));
                    }}
                    onCreateOption={(inputValue) => {
                      const email = inputValue.trim().toLowerCase();
                      if (!EMAIL_REGEX.test(email)) {
                        setValidationError((prev) => ({
                          ...prev,
                          cc: [`"${inputValue}" is not a valid email address.`],
                        }));
                        return;
                      }
                      setCcEmails((prev) => [...prev, { value: email, label: email }]);
                      setValidationError((prev) => ({ ...prev, cc: undefined }));
                    }}
                    noOptionsMessage={() => "Type an email address and press Enter"}
                    formatCreateLabel={(input) => `Add "${input}"`}
                    components={{ DropdownIndicator: null }}
                  />
                  {validationError.cc && (
                    <small className="text-danger d-block mt-1">{validationError.cc[0]}</small>
                  )}
                  {validationError["cc.0"] && (
                    <small className="text-danger d-block mt-1">{validationError["cc.0"][0]}</small>
                  )}
                </Form.Group>
              </Col>

              <Col md={12} lg={12} xs={12} sm={12} className="mb-2">
                <Form.Group className="mb-3" controlId="formBody">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Write your message..."
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    isInvalid={!!validationError.body}
                    required
                  />
                  {validationError.body && (
                    <Form.Control.Feedback type="invalid">
                      {validationError.body[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button
              style={{ backgroundColor: "#c4281b", color: "#fff" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
